import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { Flame, UtensilsCrossed, Camera, Wallet, QrCode, CalendarCheck, Trophy, MessageSquare, ShoppingBag, EyeOff, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { haptics } from "@/lib/haptics";
import { supabase } from "@/integrations/supabase/client";
import TodayRevenuePill from "@/components/dashboard/TodayRevenuePill";

type Tone = {
  bg: string;
  text: string;
  ring: string;
  iconIdle: string;
};

type Tab = {
  id: string;
  to: string;
  icon: any;
  label: string;
  requiresAuth?: boolean;
  tone: Tone;
  matchPaths?: string[];
  badgeKey?: "messages" | "bookings" | "loyalty" | "pickup";
  longPressTo?: string;
  live?: boolean;
};

const guestTabs: Tab[] = [
  { id: "deals",       to: "/home",            icon: Flame,            label: "Deals",       tone: { bg: "#FF6B35", text: "#000", ring: "rgba(255,107,53,0.45)", iconIdle: "#FF6B35" } },
  { id: "restaurants", to: "/restaurants",     icon: UtensilsCrossed,  label: "Restaurants", tone: { bg: "#9B5DE5", text: "#fff", ring: "rgba(155,93,229,0.45)", iconIdle: "#9B5DE5" } },
  { id: "social",      to: "/social",          icon: Camera,           label: "FoodGram",    tone: { bg: "#F15BB5", text: "#fff", ring: "rgba(241,91,181,0.45)", iconIdle: "#F15BB5" } },
  { id: "wallet",      to: "/guest-dashboard", icon: Wallet,           label: "Mein Wallet", requiresAuth: true, tone: { bg: "#FFE156", text: "#000", ring: "rgba(255,225,86,0.5)", iconIdle: "#E0B800" } },
];

const merchantTone: Tone = {
  bg: "hsl(var(--primary))",
  text: "hsl(var(--primary-foreground))",
  ring: "hsl(var(--primary) / 0.35)",
  iconIdle: "hsl(var(--primary))",
};

const merchantPrimaryTabs: Tab[] = [
  { id: "scanner",  to: "/dashboard/scanner",   icon: QrCode,        label: "Scannen",     tone: merchantTone, longPressTo: "/dashboard/scanner?camera=1" },
  { id: "bookings", to: "/dashboard/floorplan", icon: CalendarCheck, label: "Buchungen",   tone: merchantTone, badgeKey: "bookings", live: true },
  { id: "loyalty",  to: "/dashboard/loyalty",   icon: Trophy,        label: "Loyalty",     tone: merchantTone, badgeKey: "loyalty",  live: true },
  { id: "pickup",   to: "/dashboard/pickup",    icon: ShoppingBag,   label: "Abholung",    tone: merchantTone, badgeKey: "pickup" },
  { id: "messages", to: "/dashboard/messages",  icon: MessageSquare, label: "Nachrichten", tone: merchantTone, badgeKey: "messages", longPressTo: "/dashboard/messages?last=1" },
];

const ORDER_KEY = "merchant_nav_order_v1";
const LABELS_KEY = "merchant_nav_labels_visible_v1";

function useReorderableTabs(base: Tab[], enabled: boolean): [Tab[], (ids: string[]) => void] {
  const [order, setOrder] = useState<string[] | null>(() => {
    if (!enabled || typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      return raw ? (JSON.parse(raw) as string[]) : null;
    } catch { return null; }
  });

  const tabs = order
    ? [...base].sort((a, b) => {
        const ia = order.indexOf(a.id);
        const ib = order.indexOf(b.id);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      })
    : base;

  const save = (ids: string[]) => {
    setOrder(ids);
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(ids)); } catch { /* noop */ }
  };

  return [tabs, save];
}

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isMerchantArea = location.pathname.startsWith("/dashboard");
  // Verstecke Bottom-Nav auf Routen mit eigener Sticky-CTA / Vollbild-Flow
  const hideOnRoutes = [/^\/deal\//, /^\/auth/, /^\/onboarding/, /^\/checkin/];
  const shouldHideForRoute = hideOnRoutes.some((r) => r.test(location.pathname));

  // 7. Labels-Hide toggle (power user)
  const [labelsVisible, setLabelsVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(LABELS_KEY) !== "0";
  });
  const toggleLabels = () => {
    setLabelsVisible((v) => {
      const next = !v;
      try { localStorage.setItem(LABELS_KEY, next ? "1" : "0"); } catch { /* noop */ }
      haptics.light();
      return next;
    });
  };

  // 8. Reorderable tabs
  const [orderedMerchantTabs, saveOrder] = useReorderableTabs(merchantPrimaryTabs, isMerchantArea);
  const tabs = isMerchantArea ? orderedMerchantTabs : guestTabs;

  // 4. Auto-hide on scroll down
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY.current;
        if (y < 40) setHidden(false);
        else if (delta > 8) setHidden(true);
        else if (delta < -8) setHidden(false);
        lastY.current = y;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  // 1. Unread badges (merchant only) – polled every 45s
  const [badges, setBadges] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!isMerchantArea || !user) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [msgRes, bookRes, pickupRes] = await Promise.all([
          supabase.from("conversations").select("unread_restaurant").gt("unread_restaurant", 0),
          (supabase.from("vouchers") as any).select("id", { count: "exact", head: true }).eq("status", "ACTIVE").gte("redemption_date", today),
          (supabase.from("vouchers") as any).select("id", { count: "exact", head: true }).eq("status", "ACTIVE").eq("delivery_method", "pickup"),
        ]);
        if (cancelled) return;
        const messages = (msgRes.data || []).reduce((sum, c: any) => sum + (c.unread_restaurant || 0), 0);
        setBadges({
          messages,
          bookings: bookRes.count || 0,
          pickup: pickupRes.count || 0,
        });
      } catch { /* silent */ }
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 45_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isMerchantArea, user]);

  // 2. Active tab pop animation – track which tab was just activated
  const [poppedId, setPoppedId] = useState<string | null>(null);
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      const active = tabs.find((t) => location.pathname === t.to || location.pathname.startsWith(t.to + "/"));
      if (active) {
        setPoppedId(active.id);
        const t = setTimeout(() => setPoppedId(null), 380);
        return () => clearTimeout(t);
      }
      prevPath.current = location.pathname;
    }
  }, [location.pathname, tabs]);

  const isActive = (tab: Tab) => {
    const candidates = tab.matchPaths ?? [tab.to.split("?")[0]];
    return candidates.some(
      (path) => location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  // 5. Active-tab accent for top border glow
  const activeTab = tabs.find(isActive);
  const accentColor = activeTab
    ? (activeTab.tone.bg.startsWith("hsl") ? "hsl(var(--primary))" : activeTab.tone.bg)
    : "hsl(var(--primary))";

  // 3 + 8. Long-press: shortcut OR start reorder mode
  const [reorderMode, setReorderMode] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  const startLongPress = useCallback((tab: Tab) => {
    longPressFired.current = false;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      haptics.medium();
      if (tab.longPressTo) {
        navigate(tab.longPressTo);
      } else if (isMerchantArea) {
        setReorderMode(true);
      }
    }, 550);
  }, [navigate, isMerchantArea]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  // 8. Reorder via drag in reorder mode
  const dragId = useRef<string | null>(null);
  const handleDragStart = (id: string) => { dragId.current = id; };
  const handleDragOver = (e: React.DragEvent, overId: string) => {
    e.preventDefault();
    if (!dragId.current || dragId.current === overId) return;
    const ids = tabs.map((t) => t.id);
    const from = ids.indexOf(dragId.current);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    saveOrder(ids);
  };

  if (shouldHideForRoute) return null;

  return (
    <>
      {isMerchantArea && !hidden && <TodayRevenuePill />}
      <nav
        aria-label="Hauptnavigation"
        className="fixed bottom-0 left-0 right-0 z-[60] lg:hidden safe-area-bottom transition-transform duration-300 will-change-transform"
        style={{
          transform: hidden && !reorderMode ? "translateY(110%)" : "translateY(0)",
          background: "hsl(var(--background) / 0.98)",
          borderTop: `2px solid ${accentColor}33`,
          boxShadow: `0 -6px 24px -4px ${accentColor}30, 0 -2px 8px rgba(0,0,0,0.12)`,
        }}
      >
        {reorderMode && (
          <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-foreground text-background text-[11px] font-bold shadow-lg flex items-center gap-2">
            Reihenfolge ändern – ziehen
            <button
              onClick={() => { setReorderMode(false); haptics.light(); }}
              className="ml-1 underline underline-offset-2"
            >
              Fertig
            </button>
          </div>
        )}

        <div className="flex items-center justify-around h-16 px-1 pb-1.5 pt-1.5">
          {tabs.map((tab) => {
            const to = tab.requiresAuth && !user ? "/auth" : tab.to;
            const active = isActive(tab);
            const Icon = tab.icon;
            const tone = tab.tone;
            const badge = tab.badgeKey ? badges[tab.badgeKey] || 0 : 0;
            const popped = poppedId === tab.id;

            const inner = (
              <>
                {active && (
                  <span
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 h-1 w-8 rounded-b-full"
                    style={{ background: tone.bg.startsWith("hsl") ? "hsl(var(--primary))" : tone.bg }}
                  />
                )}
                <span
                  className="relative flex items-center justify-center rounded-xl"
                  style={{
                    width: labelsVisible ? 38 : 44,
                    height: labelsVisible ? 32 : 38,
                    background: active
                      ? tone.bg
                      : (tone.bg.startsWith("hsl") ? "transparent" : `${tone.bg}1F`),
                    border: active
                      ? `1.5px solid ${tone.bg}`
                      : `1.5px solid ${tone.bg.startsWith("hsl") ? "hsl(var(--primary) / 0.25)" : `${tone.bg}55`}`,
                    transform: popped ? "scale(1.18)" : "scale(1)",
                    transition: "transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1), width 200ms, height 200ms",
                  }}
                >
                  <Icon
                    size={labelsVisible ? 20 : 22}
                    strokeWidth={active ? 2.6 : 2}
                    color={active ? tone.text : tone.iconIdle}
                  />

                  {/* 6. Live indicator dot */}
                  {tab.live && !active && (
                    <span
                      aria-hidden="true"
                      className="absolute -top-0.5 -left-0.5 h-2 w-2 rounded-full"
                      style={{
                        background: "hsl(142 71% 45%)",
                        boxShadow: "0 0 0 2px hsl(var(--background))",
                        animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                      }}
                    />
                  )}

                  {/* 1. Unread badge */}
                  {badge > 0 && (
                    <span
                      aria-label={`${badge} ungelesen`}
                      className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-black"
                      style={{
                        background: "hsl(0 84% 60%)",
                        color: "white",
                        boxShadow: "0 0 0 2px hsl(var(--background))",
                      }}
                    >
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>

                {labelsVisible && (
                  <span
                    className="leading-tight truncate max-w-full text-center"
                    style={{
                      fontSize: "10px",
                      fontWeight: active ? 800 : 600,
                      fontFamily: active && !isMerchantArea ? "'Bangers', 'Impact', sans-serif" : "inherit",
                      letterSpacing: active && !isMerchantArea ? "0.04em" : "0",
                      color: active ? (tone.bg.startsWith("hsl") ? "hsl(var(--primary))" : tone.bg) : "hsl(var(--muted-foreground))",
                    }}
                  >
                    {tab.label}
                  </span>
                )}
              </>
            );

            const baseClasses =
              "flex flex-col items-center justify-center gap-1 flex-1 h-full relative min-w-0 px-0.5 active:scale-95 transition-transform";

            // In reorder mode → render draggable button instead of Link
            if (reorderMode) {
              return (
                <button
                  key={tab.id}
                  draggable
                  onDragStart={() => handleDragStart(tab.id)}
                  onDragOver={(e) => handleDragOver(e, tab.id)}
                  onDragEnd={() => { dragId.current = null; }}
                  className={baseClasses + " cursor-grab active:cursor-grabbing animate-pulse"}
                >
                  {inner}
                </button>
              );
            }

            return (
              <Link
                key={tab.id}
                to={to}
                onClick={(e) => {
                  if (longPressFired.current) {
                    e.preventDefault();
                    longPressFired.current = false;
                    return;
                  }
                  haptics.selection();
                }}
                onTouchStart={() => startLongPress(tab)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                onTouchCancel={cancelLongPress}
                onContextMenu={(e) => e.preventDefault()}
                className={baseClasses}
              >
                {inner}
              </Link>
            );
          })}
        </div>

        {/* 7. Labels toggle (merchant only) */}
        {isMerchantArea && !reorderMode && (
          <button
            onClick={toggleLabels}
            aria-label={labelsVisible ? "Labels ausblenden" : "Labels einblenden"}
            className="absolute -top-7 right-2 h-6 w-6 rounded-full bg-card border border-border shadow-sm flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            {labelsVisible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
        )}
      </nav>

      <div className="h-24 lg:hidden" aria-hidden="true" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} />
    </>
  );
};

export default MobileBottomNav;
